// ─── WEB AUDIO SOUND ENGINE ──────────────────────────────────
// All sounds generated with Web Audio API — no files needed.

let ctx = null;
function getCtx() {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  return ctx;
}

let masterVolume = 0.8;
export function setMasterVolume(v) { masterVolume = Math.max(0, Math.min(1, v)); }

// ─── CORE PRIMITIVES ──────────────────────────────────────────
function playTone({ frequency = 440, type = "sine", duration = 0.15, gain = 0.3, delay = 0, ramp = "down", detune = 0 }) {
  try {
    const c = getCtx();
    const osc = c.createOscillator();
    const env = c.createGain();
    const vol = c.createGain();
    osc.connect(env); env.connect(vol); vol.connect(c.destination);
    vol.gain.value     = masterVolume;
    osc.type           = type;
    osc.frequency.setValueAtTime(frequency, c.currentTime + delay);
    osc.detune.value   = detune;
    env.gain.setValueAtTime(0, c.currentTime + delay);
    env.gain.linearRampToValueAtTime(gain, c.currentTime + delay + 0.012);
    if (ramp === "down") {
      env.gain.linearRampToValueAtTime(0, c.currentTime + delay + duration);
    } else if (ramp === "exp") {
      env.gain.setTargetAtTime(0, c.currentTime + delay + duration * 0.3, duration * 0.25);
    } else {
      env.gain.setValueAtTime(gain, c.currentTime + delay + duration - 0.02);
      env.gain.linearRampToValueAtTime(0, c.currentTime + delay + duration);
    }
    osc.start(c.currentTime + delay);
    osc.stop(c.currentTime + delay + duration + 0.05);
  } catch (e) {}
}

function playFreqSweep({ startFreq, endFreq, type = "sine", duration = 0.3, gain = 0.2, delay = 0 }) {
  try {
    const c = getCtx();
    const osc = c.createOscillator();
    const env = c.createGain();
    const vol = c.createGain();
    osc.connect(env); env.connect(vol); vol.connect(c.destination);
    vol.gain.value = masterVolume;
    osc.type = type;
    osc.frequency.setValueAtTime(startFreq, c.currentTime + delay);
    osc.frequency.exponentialRampToValueAtTime(endFreq, c.currentTime + delay + duration);
    env.gain.setValueAtTime(0, c.currentTime + delay);
    env.gain.linearRampToValueAtTime(gain, c.currentTime + delay + 0.02);
    env.gain.linearRampToValueAtTime(0, c.currentTime + delay + duration);
    osc.start(c.currentTime + delay);
    osc.stop(c.currentTime + delay + duration + 0.05);
  } catch (e) {}
}

function playNoise({ duration = 0.15, gain = 0.3, filterFreq = 800, filterQ = 0.5, delay = 0 }) {
  try {
    const c    = getCtx();
    const buf  = c.createBuffer(1, Math.ceil(c.sampleRate * duration), c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = c.createBufferSource();
    const flt = c.createBiquadFilter();
    const env = c.createGain();
    const vol = c.createGain();
    flt.type = "bandpass"; flt.frequency.value = filterFreq; flt.Q.value = filterQ;
    src.buffer = buf;
    src.connect(flt); flt.connect(env); env.connect(vol); vol.connect(c.destination);
    vol.gain.value = masterVolume;
    env.gain.setValueAtTime(gain, c.currentTime + delay);
    env.gain.linearRampToValueAtTime(0, c.currentTime + delay + duration);
    src.start(c.currentTime + delay);
  } catch (e) {}
}

// ─── UI SOUNDS ────────────────────────────────────────────────
export function playClickSound() {
  // crisp double-tick
  playTone({ frequency: 1000, type: "sine",   duration: 0.045, gain: 0.12 });
  playTone({ frequency: 1400, type: "sine",   duration: 0.03,  gain: 0.08, delay: 0.04 });
}

export function playToggleOnSound() {
  playTone({ frequency: 550,  type: "sine",   duration: 0.06, gain: 0.1  });
  playTone({ frequency: 880,  type: "sine",   duration: 0.06, gain: 0.09, delay: 0.055 });
  playTone({ frequency: 1100, type: "sine",   duration: 0.05, gain: 0.07, delay: 0.11  });
}

export function playToggleOffSound() {
  playTone({ frequency: 1100, type: "sine",   duration: 0.06, gain: 0.1  });
  playTone({ frequency: 660,  type: "sine",   duration: 0.06, gain: 0.08, delay: 0.055 });
  playTone({ frequency: 440,  type: "sine",   duration: 0.05, gain: 0.06, delay: 0.11  });
}

export function playCloseSound() {
  playTone({ frequency: 600,  type: "sine",   duration: 0.06, gain: 0.09 });
  playTone({ frequency: 380,  type: "sine",   duration: 0.08, gain: 0.06, delay: 0.05 });
}

export function playOpenSound() {
  playTone({ frequency: 380,  type: "sine",   duration: 0.06, gain: 0.09 });
  playTone({ frequency: 600,  type: "sine",   duration: 0.08, gain: 0.1,  delay: 0.055 });
}

export function playTabSound() {
  playTone({ frequency: 700,  type: "sine",   duration: 0.04, gain: 0.07 });
  playTone({ frequency: 950,  type: "sine",   duration: 0.04, gain: 0.06, delay: 0.04 });
}

export function playSliderSound() {
  playTone({ frequency: 680 + Math.random() * 120, type: "sine", duration: 0.035, gain: 0.05 });
}

export function playWarningSound() {
  playNoise({ duration: 0.09, gain: 0.2, filterFreq: 280, filterQ: 1 });
  playTone({ frequency: 220, type: "sawtooth", duration: 0.2,  gain: 0.22 });
  playTone({ frequency: 196, type: "sawtooth", duration: 0.2,  gain: 0.16, delay: 0.12 });
}

export function playFavouriteSound() {
  playTone({ frequency: 880,  type: "sine",   duration: 0.07, gain: 0.12 });
  playTone({ frequency: 1100, type: "sine",   duration: 0.09, gain: 0.10, delay: 0.06 });
  playTone({ frequency: 1320, type: "sine",   duration: 0.08, gain: 0.08, delay: 0.14 });
  playTone({ frequency: 1760, type: "sine",   duration: 0.07, gain: 0.06, delay: 0.22 });
}

export function playSubmitSound() {
  playNoise({ duration: 0.04, gain: 0.1, filterFreq: 1400, filterQ: 3 });
  playTone({ frequency: 523,  type: "triangle", duration: 0.12, gain: 0.16 });
  playTone({ frequency: 784,  type: "sine",     duration: 0.14, gain: 0.14, delay: 0.10 });
  playTone({ frequency: 1047, type: "sine",     duration: 0.12, gain: 0.12, delay: 0.22 });
}

export function playClaimSound() {
  playNoise({ duration: 0.05, gain: 0.1, filterFreq: 2000, filterQ: 2 });
  [[440, 0], [554, 0.07], [659, 0.14], [880, 0.22]].forEach(([f, d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.22, gain: 0.16, delay: d, ramp: "exp" })
  );
}

// ─── ROLL SOUND ────────────────────────────────────────────────
// Big satisfying whoosh + thud
export function playRollSound() {
  // whoosh
  playFreqSweep({ startFreq: 120, endFreq: 600,  type: "sine",     duration: 0.22, gain: 0.18, delay: 0.0  });
  playFreqSweep({ startFreq: 600, endFreq: 120,  type: "sine",     duration: 0.14, gain: 0.12, delay: 0.22 });
  // body
  playNoise({ duration: 0.28, gain: 0.3,  filterFreq: 500, filterQ: 0.6 });
  // thud
  playTone({ frequency: 100, type: "sine", duration: 0.18, gain: 0.28, delay: 0.20, ramp: "exp" });
  playTone({ frequency: 60,  type: "sine", duration: 0.12, gain: 0.18, delay: 0.28, ramp: "exp" });
}

// ─── RARITY SOUNDS ────────────────────────────────────────────
// Common — soft muted thud, understated
export function playCommonSound() {
  playNoise({ duration: 0.08, gain: 0.14, filterFreq: 300, filterQ: 1.2 });
  playTone({ frequency: 220, type: "sine", duration: 0.18, gain: 0.16, ramp: "exp" });
  playTone({ frequency: 330, type: "sine", duration: 0.12, gain: 0.10, delay: 0.08, ramp: "exp" });
}

// Uncommon — clean two-note chime, confident not playful
export function playUncommonSound() {
  playNoise({ duration: 0.05, gain: 0.1, filterFreq: 1200, filterQ: 2 });
  playTone({ frequency: 440, type: "triangle", duration: 0.22, gain: 0.18, ramp: "exp" });
  playTone({ frequency: 660, type: "sine",     duration: 0.18, gain: 0.14, delay: 0.10, ramp: "exp" });
  playTone({ frequency: 880, type: "sine",     duration: 0.12, gain: 0.09, delay: 0.20, ramp: "exp" });
}

// Rare — crystalline shimmer, high quality feel
export function playRareSound() {
  playNoise({ duration: 0.07, gain: 0.14, filterFreq: 2200, filterQ: 2.5 });
  playTone({ frequency: 293, type: "sine",     duration: 0.14, gain: 0.22, ramp: "exp" });
  playTone({ frequency: 587, type: "triangle", duration: 0.32, gain: 0.20, delay: 0.05, ramp: "exp" });
  playTone({ frequency: 880, type: "sine",     duration: 0.26, gain: 0.15, delay: 0.14, ramp: "exp" });
  playTone({ frequency:1174, type: "sine",     duration: 0.18, gain: 0.10, delay: 0.24, ramp: "exp" });
  playFreqSweep({ startFreq: 1800, endFreq: 3200, type: "sine", duration: 0.3, gain: 0.05, delay: 0.28 });
}

// Epic — deep cinematic impact + rising chord
export function playEpicSound() {
  playTone({ frequency: 80, type: "sine", duration: 0.35, gain: 0.30, ramp: "exp" });
  playNoise({ duration: 0.14, gain: 0.22, filterFreq: 250, filterQ: 0.7 });
  [[220,0.06],[277,0.14],[330,0.22],[415,0.32],[523,0.44]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.40, gain: 0.18, delay: d, ramp: "exp" })
  );
  playTone({ frequency: 1046, type: "sine", duration: 0.50, gain: 0.12, delay: 0.55, ramp: "exp" });
  playFreqSweep({ startFreq: 800, endFreq: 1600, type: "sine", duration: 0.35, gain: 0.08, delay: 0.48 });
}

// Legendary — full brass fanfare, triumphant
export function playLegendarySound() {
  playTone({ frequency: 65,  type: "sine", duration: 0.45, gain: 0.35, ramp: "exp" });
  playTone({ frequency: 98,  type: "sine", duration: 0.30, gain: 0.22, delay: 0.06, ramp: "exp" });
  playNoise({ duration: 0.12, gain: 0.28, filterFreq: 200, filterQ: 0.6 });
  [[196,0.08],[247,0.16],[294,0.24],[370,0.34],[494,0.46],[622,0.58],[740,0.70]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.48, gain: 0.20, delay: d, ramp: "exp" })
  );
  [[1047,0.82],[1319,0.94],[1568,1.06]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "sine", duration: 0.30, gain: 0.13, delay: d, ramp: "exp" })
  );
  playFreqSweep({ startFreq: 1200, endFreq: 2800, type: "sine", duration: 0.5, gain: 0.09, delay: 1.08 });
}

// Mythic — orchestral swell, deep and powerful
export function playMythicSound() {
  playTone({ frequency: 55,  type: "sine", duration: 0.60, gain: 0.38, ramp: "exp" });
  playTone({ frequency: 82,  type: "sine", duration: 0.45, gain: 0.24, delay: 0.04, ramp: "exp" });
  playNoise({ duration: 0.18, gain: 0.30, filterFreq: 180, filterQ: 0.5 });
  [[130,0.06],[164,0.14],[196,0.22],[246,0.32],[311,0.43],[392,0.55],[494,0.68],[622,0.82]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "sawtooth", duration: 0.52, gain: 0.17, delay: d, ramp: "exp" })
  );
  [[1047,0.96],[1319,1.10],[1568,1.24],[1760,1.38]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "sine", duration: 0.34, gain: 0.13, delay: d, ramp: "exp" })
  );
  playFreqSweep({ startFreq: 1600, endFreq: 3600, type: "sine", duration: 0.55, gain: 0.08, delay: 1.42 });
}

// Ultra — cinematic hit, full frequency spectrum
export function playUltraSound() {
  playTone({ frequency: 40,  type: "sine", duration: 0.90, gain: 0.50, ramp: "exp" });
  playTone({ frequency: 55,  type: "sine", duration: 0.70, gain: 0.34, delay: 0.04, ramp: "exp" });
  playTone({ frequency: 80,  type: "sine", duration: 0.50, gain: 0.22, delay: 0.08, ramp: "exp" });
  playNoise({ duration: 0.24, gain: 0.42, filterFreq: 350, filterQ: 0.5 });
  [[98,0.08],[123,0.16],[155,0.24],[196,0.32],[246,0.40],
   [311,0.48],[392,0.57],[494,0.67],[622,0.78],[784,0.90]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.46, gain: 0.22, delay: d, ramp: "exp" })
  );
  [[1047,1.02],[1175,1.12],[1319,1.22],[1568,1.34],[1760,1.46],[2093,1.58]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "sine", duration: 0.26, gain: 0.14, delay: d, ramp: "exp" })
  );
  playFreqSweep({ startFreq: 2200, endFreq: 5000, type: "sine", duration: 0.55, gain: 0.10, delay: 1.62 });
}
// ─── MASTER DISPATCHER ────────────────────────────────────────
export function playRaritySound(rarity) {
  const map = {
    Common: playCommonSound, Uncommon: playUncommonSound, Rare: playRareSound,
    Epic: playEpicSound, Legendary: playLegendarySound, Mythic: playMythicSound, Ultra: playUltraSound,
  };
  (map[rarity] || playCommonSound)();
}

// ─── ECONOMY SOUNDS ───────────────────────────────────────────
export function playCoinSound() {
  // satisfying triple jingle
  playTone({ frequency: 1047, type: "sine", duration: 0.09,  gain: 0.18 });
  playTone({ frequency: 1319, type: "sine", duration: 0.09,  gain: 0.15, delay: 0.07 });
  playTone({ frequency: 1568, type: "sine", duration: 0.11,  gain: 0.12, delay: 0.14 });
  playTone({ frequency: 2093, type: "sine", duration: 0.09,  gain: 0.08, delay: 0.22 });
}

export function playPurchaseSound() {
  playNoise({ duration: 0.04, gain: 0.1, filterFreq: 1800, filterQ: 3 });
  playTone({ frequency: 300, type: "sine",     duration: 0.12, gain: 0.22, ramp: "exp" });
  playTone({ frequency: 600, type: "triangle", duration: 0.14, gain: 0.18, delay: 0.09 });
  playTone({ frequency: 900, type: "sine",     duration: 0.12, gain: 0.14, delay: 0.20 });
  playTone({ frequency: 1200,type: "sine",     duration: 0.10, gain: 0.09, delay: 0.30 });
}

export function playAchievementSound() {
  playNoise({ duration: 0.06, gain: 0.12, filterFreq: 1600, filterQ: 3 });
  [[523,0.04],[659,0.13],[784,0.23],[1047,0.35],[1319,0.49],[1568,0.63]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "sine", duration: 0.32, gain: 0.17, delay: d, ramp: "exp" })
  );
}

export function playMissionSound() {
  playTone({ frequency: 440,  type: "triangle", duration: 0.11, gain: 0.20 });
  playTone({ frequency: 660,  type: "triangle", duration: 0.11, gain: 0.18, delay: 0.09 });
  playTone({ frequency: 880,  type: "sine",     duration: 0.18, gain: 0.16, delay: 0.19 });
  playTone({ frequency: 1320, type: "sine",     duration: 0.24, gain: 0.13, delay: 0.34 });
  playTone({ frequency: 1760, type: "sine",     duration: 0.18, gain: 0.09, delay: 0.52 });
}

export function playPrestigeSound() {
  // epic ascending scale — feels like a level-up cinematic
  playTone({ frequency: 75, type: "sine", duration: 0.5, gain: 0.35, ramp: "exp" });
  playNoise({ duration: 0.12, gain: 0.18, filterFreq: 250, filterQ: 0.7 });
  [[261,0.08],[329,0.18],[415,0.29],[523,0.41],[659,0.55],[830,0.70],[1046,0.85],[1319,1.00],[1568,1.16]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.52, gain: 0.17, delay: d, ramp: "exp" })
  );
  playFreqSweep({ startFreq: 1600, endFreq: 3200, type: "sine", duration: 0.65, gain: 0.11, delay: 1.25 });
  playNoise({ duration: 0.10, gain: 0.15, filterFreq: 600, filterQ: 2.5, delay: 1.30 });
}

export function playStreakSound() {
  playNoise({ duration: 0.06, gain: 0.14, filterFreq: 1000, filterQ: 2 });
  [[330,0],[440,0.08],[550,0.16],[660,0.26],[880,0.38],[1100,0.50]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.28, gain: 0.18, delay: d, ramp: "exp" })
  );
}

// ─── BATTLE SOUNDS ────────────────────────────────────────────
export function playBattleStartSound() {
  playNoise({ duration: 0.09, gain: 0.22, filterFreq: 300, filterQ: 0.7 });
  playTone({ frequency: 220, type: "sine",     duration: 0.20, gain: 0.26, ramp: "exp" });
  [[330,0.12],[440,0.22],[550,0.33],[660,0.44]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.30, gain: 0.19, delay: d, ramp: "exp" })
  );
  playTone({ frequency: 880, type: "sine",     duration: 0.45, gain: 0.22, delay: 0.56, ramp: "exp" });
}

export function playStatSelectSound() {
  playNoise({ duration: 0.04, gain: 0.14, filterFreq: 2600, filterQ: 4 });
  playTone({ frequency: 660,  type: "square",   duration: 0.07, gain: 0.13 });
  playTone({ frequency: 990,  type: "sine",     duration: 0.09, gain: 0.11, delay: 0.04 });
  playTone({ frequency: 1320, type: "sine",     duration: 0.07, gain: 0.08, delay: 0.09 });
}

export function playCountTick() {
  playTone({ frequency: 1100 + Math.random() * 200, type: "sine", duration: 0.03, gain: 0.06 });
}

export function playRevealSound() {
  playFreqSweep({ startFreq: 300, endFreq: 900,  type: "sine",     duration: 0.18, gain: 0.18 });
  playNoise({ duration: 0.06, gain: 0.11, filterFreq: 1200, filterQ: 2, delay: 0.10 });
  playTone({ frequency: 880, type: "triangle", duration: 0.22, gain: 0.16, delay: 0.15, ramp: "exp" });
}

export function playRoundWinSound() {
  playNoise({ duration: 0.05, gain: 0.13, filterFreq: 2100, filterQ: 3 });
  [[523,0],[659,0.08],[784,0.17],[1047,0.27]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.32, gain: 0.20, delay: d, ramp: "exp" })
  );
  playTone({ frequency: 1568, type: "sine", duration: 0.36, gain: 0.14, delay: 0.38, ramp: "exp" });
}

export function playRoundLossSound() {
  playNoise({ duration: 0.11, gain: 0.22, filterFreq: 200, filterQ: 0.6 });
  [[440,0],[370,0.1],[294,0.21],[220,0.33]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "sawtooth", duration: 0.24, gain: 0.17, delay: d, ramp: "exp" })
  );
  playTone({ frequency: 110, type: "sine", duration: 0.45, gain: 0.26, delay: 0.32, ramp: "exp" });
}

export function playBigWinSound() {
  playTone({ frequency: 60,  type: "sine", duration: 0.55, gain: 0.42, ramp: "exp" });
  playTone({ frequency: 80,  type: "sine", duration: 0.35, gain: 0.32, delay: 0.05, ramp: "exp" });
  playNoise({ duration: 0.16, gain: 0.38, filterFreq: 250, filterQ: 0.5 });
  [[523,0.10],[659,0.19],[784,0.28]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.24, gain: 0.19, delay: d, ramp: "exp" })
  );
}

export function playVictorySound() {
  playNoise({ duration: 0.14, gain: 0.24, filterFreq: 400, filterQ: 0.8 });
  playTone({ frequency: 100, type: "sine", duration: 0.4,  gain: 0.32, ramp: "exp" });
  [[523,0.08],[659,0.18],[784,0.28],[1047,0.40],[1319,0.54],[1047,0.66],[1319,0.78],[1568,0.92]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "triangle", duration: 0.40, gain: 0.21, delay: d, ramp: "exp" })
  );
  [1.06,1.16,1.26].forEach((d,i) =>
    playTone({ frequency: 1760 + i * 220, type: "sine", duration: 0.30, gain: 0.16, delay: d })
  );
  playFreqSweep({ startFreq: 2200, endFreq: 4400, type: "sine", duration: 0.55, gain: 0.11, delay: 1.40 });
}

export function playDefeatSound() {
  playNoise({ duration: 0.16, gain: 0.27, filterFreq: 180, filterQ: 0.5 });
  playTone({ frequency: 90, type: "sine", duration: 0.60, gain: 0.38, ramp: "exp" });
  [[392,0.08],[330,0.21],[262,0.36],[220,0.53],[196,0.72]].forEach(([f,d]) =>
    playTone({ frequency: f, type: "sawtooth", duration: 0.38, gain: 0.16, delay: d, ramp: "exp" })
  );
  playTone({ frequency: 130, type: "sine", duration: 0.65, gain: 0.22, delay: 0.80, ramp: "exp" });
}

export function playScratchSound() {
  playNoise({ duration: 0.07, gain: 0.14, filterFreq: 3000, filterQ: 4 });
  playTone({ frequency: 180 + Math.random() * 80, type: "sawtooth", duration: 0.06, gain: 0.05 });
}

// ─── BACKGROUND MUSIC ENGINE ──────────────────────────────────
// Chill lo-fi ambient — soft pads, bass, gentle melody
let musicCtx       = null;
let musicGain      = null;
let musicVolume    = 0.18;
let musicEnabled   = false;
let musicScheduler = null;
let musicNextBeat  = 0;
let musicStopping  = false;

export function setMusicVolume(v) {
  musicVolume = Math.max(0, Math.min(1, v));
  if (musicGain && musicCtx) {
    musicGain.gain.cancelScheduledValues(musicCtx.currentTime);
    musicGain.gain.setValueAtTime(musicVolume, musicCtx.currentTime);
  }
}

function getMusicCtx() {
  if (!musicCtx) {
    musicCtx  = new (window.AudioContext || window.webkitAudioContext)();
    musicGain = musicCtx.createGain();
    musicGain.gain.value = musicVolume;
    musicGain.connect(musicCtx.destination);
  }
  return musicCtx;
}

function schedulePad(c, out, freq, startTime, duration, gainVal = 0.07) {
  if (!freq) return;
  try {
    [-4, 0, 4].forEach(detune => {
      const osc = c.createOscillator();
      const env = c.createGain();
      osc.type = "sine"; osc.frequency.value = freq; osc.detune.value = detune;
      osc.connect(env); env.connect(out);
      env.gain.setValueAtTime(0, startTime);
      env.gain.linearRampToValueAtTime(gainVal, startTime + duration * 0.35);
      env.gain.setValueAtTime(gainVal, startTime + duration * 0.6);
      env.gain.linearRampToValueAtTime(0, startTime + duration);
      osc.start(startTime); osc.stop(startTime + duration + 0.1);
    });
  } catch(e) {}
}

function scheduleBass(c, out, freq, startTime, duration) {
  try {
    const osc = c.createOscillator();
    const env = c.createGain();
    osc.type = "sine"; osc.frequency.value = freq;
    osc.connect(env); env.connect(out);
    env.gain.setValueAtTime(0, startTime);
    env.gain.linearRampToValueAtTime(0.12, startTime + 0.08);
    env.gain.setTargetAtTime(0, startTime + duration * 0.5, duration * 0.25);
    osc.start(startTime); osc.stop(startTime + duration + 0.1);
  } catch(e) {}
}

function scheduleMelody(c, out, freq, startTime, duration) {
  if (!freq) return;
  try {
    const osc = c.createOscillator();
    const env = c.createGain();
    osc.type = "triangle"; osc.frequency.value = freq;
    osc.connect(env); env.connect(out);
    env.gain.setValueAtTime(0, startTime);
    env.gain.linearRampToValueAtTime(0.09, startTime + 0.06);
    env.gain.setTargetAtTime(0, startTime + duration * 0.4, duration * 0.3);
    osc.start(startTime); osc.stop(startTime + duration + 0.1);
  } catch(e) {}
}

function scheduleVinylNoise(c, out, startTime, duration) {
  try {
    const bufLen = Math.ceil(c.sampleRate * duration);
    const buf    = c.createBuffer(1, bufLen, c.sampleRate);
    const data   = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * 0.012;
    const src = c.createBufferSource();
    const flt = c.createBiquadFilter();
    const env = c.createGain();
    flt.type = "lowpass"; flt.frequency.value = 800;
    src.buffer = buf;
    src.connect(flt); flt.connect(env); env.connect(out);
    env.gain.setValueAtTime(0.4, startTime);
    env.gain.linearRampToValueAtTime(0, startTime + duration);
    src.start(startTime);
  } catch(e) {}
}

const BEAT = 0.92;
const BAR  = BEAT * 4;

const PROGRESSIONS = [
  { bass: 65.41, pad: [261.63, 329.63, 392.00, 493.88] },
  { bass: 55.00, pad: [220.00, 261.63, 329.63, 392.00] },
  { bass: 87.31, pad: [174.61, 220.00, 261.63, 329.63] },
  { bass: 98.00, pad: [196.00, 246.94, 293.66, 349.23] },
];

const MELODY_PHRASES = [
  [523.25, null, 587.33, null, 659.25, 587.33, null, 523.25],
  [493.88, null, null,   523.25, 587.33, null, 493.88, null],
  [null,   440.00, null, 493.88, 523.25, null, null,  440.00],
  [392.00, null, 440.00, null,   493.88, null, 392.00, null],
];

let _barIdx = 0;

function scheduleMusicBar(startTime) {
  const c      = getMusicCtx();
  const prog   = PROGRESSIONS[_barIdx % PROGRESSIONS.length];
  const melody = MELODY_PHRASES[_barIdx % MELODY_PHRASES.length];
  prog.pad.forEach(freq => schedulePad(c, musicGain, freq, startTime, BAR * 1.1, 0.055));
  scheduleBass(c, musicGain, prog.bass, startTime, BEAT * 2.5);
  melody.forEach((freq, i) => {
    if (freq) scheduleMelody(c, musicGain, freq, startTime + i * (BEAT * 0.5), BEAT * 0.7);
  });
  scheduleVinylNoise(c, musicGain, startTime, BAR);
  _barIdx++;
  return BAR;
}

export function startBackgroundMusic() {
  if (musicEnabled) return;
  musicEnabled  = true;
  musicStopping = false;
  const c = getMusicCtx();
  if (c.state === "suspended") c.resume();
  musicGain.gain.cancelScheduledValues(c.currentTime);
  musicGain.gain.setValueAtTime(0, c.currentTime);
  if (musicVolume > 0) musicGain.gain.linearRampToValueAtTime(musicVolume, c.currentTime + 3.0);
  musicNextBeat = c.currentTime + 0.3;
  function tick() {
    if (musicStopping) return;
    const now = getMusicCtx().currentTime;
    while (musicNextBeat < now + 2.5) {
      musicNextBeat += scheduleMusicBar(musicNextBeat);
    }
    musicScheduler = setTimeout(tick, 500);
  }
  tick();
}

export function stopBackgroundMusic() {
  if (!musicEnabled) return;
  musicStopping = true;
  musicEnabled  = false;
  clearTimeout(musicScheduler);
  if (musicGain && musicCtx) musicGain.gain.setTargetAtTime(0, musicCtx.currentTime, 0.8);
}

export function isMusicPlaying() { return musicEnabled; }